// Hooks de TanStack Query de Administración · Checklists (plan 2026-10-30
// Fase 3, T-ljhq). Claves propias bajo ["administracion", "checklists",
// …], al estilo de contratistas/queries.ts; cada escritura invalida
// además las claves globales de checklist-templates (queryKeys en
// lib/api/queries.ts): el panel viejo de /configuracion y su drawer
// siguen vivos en la misma sesión y tienen que ver la asignación nueva.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { ChecklistTemplateUpdateBody } from "@bitacorapro/api-types";
import { queryKeys } from "../../../lib/api/queries";
import { checklistsApi } from "./api";

export const checklistsQueryKeys = {
  list: ["administracion", "checklists", "list"] as const,
  detail: (templateId: string) => ["administracion", "checklists", templateId, "detail"] as const,
  assets: (templateId: string) => ["administracion", "checklists", templateId, "assets"] as const,
} as const;

function invalidateAfterWrite(qc: QueryClient, templateId: string): void {
  void qc.invalidateQueries({ queryKey: checklistsQueryKeys.list });
  void qc.invalidateQueries({ queryKey: checklistsQueryKeys.assets(templateId) });
  void qc.invalidateQueries({ queryKey: queryKeys.checklistTemplatesAdminList });
  void qc.invalidateQueries({ queryKey: queryKeys.checklistTemplateAssets(templateId) });
}

// Apagada para quien no administra: GET /checklist-templates responde 403
// a quien no pasa requireOrgAdminCaller, y la guarda de App.tsx redirige
// antes. Con la receta aún cargando la query espera (waitingOn).
export function useChecklistTemplates(enabled: boolean) {
  return useQuery({
    queryKey: checklistsQueryKeys.list,
    queryFn: () => checklistsApi.listAdmin(),
    enabled,
  });
}

// La estructura de la plantilla seleccionada. Sin id (lista vacía) no
// dispara.
export function useChecklistTemplateDetail(templateId: string | null) {
  return useQuery({
    queryKey: checklistsQueryKeys.detail(templateId ?? ""),
    queryFn: () => checklistsApi.getDetail(templateId as string),
    enabled: templateId !== null,
  });
}

// Qué tiendas llevan la plantilla. refetchOnMount "always" por la misma
// razón que useChecklistTemplateAssets: el PUT es un reemplazo completo y
// dos gerentes con el drawer abierto escribirían desde una lectura vieja.
export function useChecklistAssignedAssets(templateId: string | undefined) {
  return useQuery({
    queryKey: checklistsQueryKeys.assets(templateId ?? ""),
    queryFn: () => checklistsApi.getAssets(templateId as string),
    enabled: !!templateId,
    refetchOnMount: "always",
  });
}

// La respuesta ya es el conjunto nuevo: se siembra la caché de la
// plantilla y se invalida la lista por su «{n} tiendas».
export function usePutChecklistAssets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, assetIds }: { templateId: string; assetIds: string[] }) =>
      checklistsApi.putAssets(templateId, assetIds),
    onSuccess: (data, { templateId }) => {
      qc.setQueryData(checklistsQueryKeys.assets(templateId), data);
      qc.setQueryData(queryKeys.checklistTemplateAssets(templateId), data);
      invalidateAfterWrite(qc, templateId);
    },
  });
}

export function usePatchChecklistTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ templateId, body }: { templateId: string; body: ChecklistTemplateUpdateBody }) =>
      checklistsApi.patch(templateId, body),
    onSuccess: (_data, { templateId }) => invalidateAfterWrite(qc, templateId),
  });
}
